import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "../../../contexts/toast/toast-context";
import { useAuth } from "../../../hooks/use-auth";
import api from "../../../services/api";
import type {
  Campaign,
  CampaignTemplate,
  ContactField,
} from "../../../types/campaign";
import { getApiErrorMessage } from "../../../utils";
import { WhatsAppPreview } from "../../../components/whatsapp-template/whatsapp-preview";
import { createTemplatePreview } from "../../../components/whatsapp-template/template-utils";

const steps = [
  "Informações",
  "Público-alvo",
  "Mensagem",
  "Agendamento",
  "Revisão",
];
export default function CampaignWizardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get("draft");
  const toast = useToast();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [audience, setAudience] = useState<number>();
  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [fields, setFields] = useState<ContactField[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [mappings, setMappings] = useState<Record<number, string>>({});
  const [scheduleMode, setScheduleMode] = useState<"NOW" | "LATER">("NOW");
  const [scheduledAt, setScheduledAt] = useState("");
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const selected = templates.find((item) => item.id === templateId);
  useEffect(() => {
    if (!draftId) return;
    api
      .get<Campaign>(`/campaigns/${draftId}`)
      .then(({ data }) => {
        if (data.status !== "DRAFT") {
          navigate(`/painel/comercial/campanhas/${data._id}`, {
            replace: true,
          });
          return;
        }
        setId(data._id);
        setName(data.name);
        setStep(Math.min(4, Math.max(0, data.draft_step ?? 0)));
        setTemplateId(data.draft_template_id ?? "");
        setMappings(
          Object.fromEntries(
            (data.draft_parameter_mappings ?? []).map((mapping) => [
              mapping.position,
              mapping.contactField,
            ]),
          ),
        );
        setScheduleMode(data.draft_schedule_mode ?? "NOW");
        if (data.draft_scheduled_at) {
          const date = new Date(data.draft_scheduled_at);
          const local = new Date(
            date.getTime() - date.getTimezoneOffset() * 60000,
          );
          setScheduledAt(local.toISOString().slice(0, 16));
        }
      })
      .catch((err) => setError(getApiErrorMessage(err)));
  }, [draftId, navigate]);
  useEffect(() => {
    if (!id || !name.trim()) return;
    const timer = window.setTimeout(() => {
      api
        .patch(`/campaigns/${id}`, {
          name,
          step,
          templateId: templateId || undefined,
          parameterMappings: Object.entries(mappings).map(
            ([position, contactField]) => ({
              position: Number(position),
              contactField,
            }),
          ),
          scheduleMode,
          scheduledAt:
            scheduleMode === "LATER" && scheduledAt
              ? new Date(scheduledAt).toISOString()
              : undefined,
        })
        .catch((err) => setError(getApiErrorMessage(err)));
    }, 500);
    return () => window.clearTimeout(timer);
  }, [id, mappings, name, scheduleMode, scheduledAt, step, templateId]);
  useEffect(() => {
    Promise.all([
      api.get<{ count: number }>("/campaigns/audience/granted-count"),
      api.get<CampaignTemplate[]>("/campaigns/templates"),
      api.get<ContactField[]>("/campaigns/contact-fields"),
    ])
      .then(([count, templateResult, fieldResult]) => {
        setAudience(count.data.count);
        setTemplates(
          templateResult.data.filter((template) =>
            template.element_name.toLowerCase().startsWith("marketing"),
          ),
        );
        setFields(fieldResult.data);
      })
      .catch((err) => setCatalogError(getApiErrorMessage(err)))
      .finally(() => setLoadingCatalog(false));
  }, []);
  const preview = useMemo(() => {
    return createTemplatePreview(
      selected,
      Object.fromEntries(
        Object.entries(mappings).map(([position, key]) => {
          const field = fields.find((item) => item.key === key);
          return [Number(position), field ? `[${field.example}]` : undefined];
        }),
      ),
      "Olá! 👋\n\nTemos uma condição especial para você.\n\nAproveite essa oportunidade!",
    );
  }, [selected, fields, mappings]);
  const canAdvance =
    step === 0
      ? name.trim().length > 0 && name.trim().length <= 120
      : step === 2
        ? Boolean(selected) &&
          selected!.parameter_positions.every((position) => mappings[position])
        : step === 3
          ? scheduleMode === "NOW" ||
            (Boolean(scheduledAt) &&
              new Date(scheduledAt).getTime() > Date.now())
          : true;
  const next = async () => {
    if (!canAdvance) {
      setError("Preencha os campos obrigatórios para continuar.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      let draftId = id;
      if (step === 0) {
        if (!draftId) {
          const response = await api.post<Campaign>("/campaigns", { name });
          draftId = response.data._id;
          setId(draftId);
        }
      }
      const nextStep = Math.min(4, step + 1);
      await api.patch(`/campaigns/${draftId}`, {
        name,
        step: nextStep,
        templateId: templateId || undefined,
        parameterMappings: Object.entries(mappings).map(
          ([position, contactField]) => ({
            position: Number(position),
            contactField,
          }),
        ),
        scheduleMode,
        scheduledAt:
          scheduleMode === "LATER" && scheduledAt
            ? new Date(scheduledAt).toISOString()
            : undefined,
      });
      setStep(nextStep);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };
  const goBack = async () => {
    const previousStep = Math.max(0, step - 1);
    setStep(previousStep);
    if (!id) return;
    try {
      await api.patch(`/campaigns/${id}`, { step: previousStep });
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  };
  const confirm = async () => {
    if (!id || !selected || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/campaigns/${id}/confirm`, {
        templateId: selected.id,
        parameterMappings: selected.parameter_positions.map((position) => ({
          position,
          contactField: mappings[position],
        })),
        scheduledAt:
          scheduleMode === "LATER"
            ? new Date(scheduledAt).toISOString()
            : undefined,
      });
      toast.success("Campanha confirmada e agendada.");
      navigate(`/painel/comercial/campanhas/${id}`);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };
  const title = [
    "Informações da campanha",
    "Selecionar público-alvo",
    "Mensagem da campanha",
    "Quando deseja enviar?",
    "Revisar campanha",
  ][step];
  const subtitle = [
    "Comece identificando a sua campanha.",
    "Escolha qual segmento receberá a campanha.",
    "Selecione um modelo aprovado no WhatsApp.",
    "Defina o momento do disparo.",
    "Confira as configurações antes de confirmar.",
  ][step];
  return (
    <section className="mx-auto max-w-[980px] p-3 sm:p-5">
      <button
        className="mb-2 flex items-center gap-1 text-xs text-slate-500 hover:text-blue-700"
        onClick={() => navigate("/painel/comercial/campanhas")}
      >
        <ArrowLeft size={13} /> Campanhas
      </button>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Nova campanha</h1>
          <p className="mt-1 text-sm text-slate-500">
            Configure o disparo para seus contatos
          </p>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-bold text-slate-600">
          Rascunho
        </span>
      </div>
      <ol
        className="my-7 flex flex-wrap items-center gap-y-3"
        aria-label="Etapas da campanha"
      >
        {steps.map((label, index) => (
          <li
            key={label}
            className="flex items-center"
            aria-current={step === index ? "step" : undefined}
          >
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold ${step === index ? "border-blue-600 bg-blue-600 text-white" : index < step ? "border-blue-300 bg-blue-50 text-blue-700" : "border-slate-300 bg-white text-slate-500"}`}
            >
              {index < step ? <Check size={13} /> : index + 1}
            </span>
            <span
              className={`ml-2 text-xs font-semibold ${step === index ? "text-blue-700" : "text-slate-500"}`}
            >
              {label}
            </span>
            {index < steps.length - 1 && (
              <span className="mx-3 h-px w-6 bg-slate-300 sm:w-10" />
            )}
          </li>
        ))}
      </ol>
      <div className="grid gap-5 md:grid-cols-[1fr_340px]">
        <div className="flex min-h-[455px] flex-col rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="text-base font-bold">{title}</h2>
          <p className="mb-5 mt-1 text-xs leading-5 text-slate-500">
            {subtitle}
          </p>
          {step === 0 && (
            <div className="space-y-5">
              <label className="block">
                <span className="form-label text-xs">Nome da campanha</span>
                <input
                  autoFocus
                  maxLength={120}
                  className="form-input"
                  placeholder="Promoção de Outubro"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <div>
                <span className="form-label text-xs">Canal de envio</span>
                <div className="flex items-center gap-3 rounded-xl border border-blue-500 bg-blue-50 p-4">
                  <span className="h-4 w-4 rounded-full border-4 border-white bg-blue-500 ring-1 ring-blue-500" />
                  <span>
                    <strong className="block text-sm">WhatsApp Business</strong>
                    <small className="text-slate-500">
                      Envio por API oficial
                    </small>
                  </span>
                </div>
              </div>
              <p className="rounded-xl border bg-slate-50 p-4 text-[11px] leading-5 text-slate-500">
                O nome da campanha é interno e não aparece para os clientes.
              </p>
            </div>
          )}
          {step === 1 && (
            <>
              <label className="flex items-center gap-3 rounded-xl border bg-white p-4 shadow-sm">
                <input
                  type="radio"
                  className="h-4 w-4 accent-blue-600"
                  checked
                  readOnly
                />
                <span className="flex-1">
                  <strong className="block text-sm">
                    Todos os contatos com aceite
                  </strong>
                  <small className="text-slate-500">
                    Contatos com consentimento de marketing vigente
                  </small>
                </span>
                <strong className="text-sm">{audience ?? "..."} aptos</strong>
              </label>
              <p className="mt-auto rounded-xl border bg-slate-50 p-4 text-[11px] leading-5 text-slate-500">
                Contatos sem consentimento, com bloqueio ou opt-out são
                excluídos da seleção. A quantidade será revalidada no envio.
              </p>
            </>
          )}
          {step === 2 &&
            (loadingCatalog ? (
              <p className="text-sm">Carregando templates...</p>
            ) : catalogError ? (
              <p role="alert" className="text-sm text-red-700">
                {catalogError}
              </p>
            ) : !templates.length ? (
              <p className="text-sm">
                Nenhum template aprovado está disponível.
              </p>
            ) : (
              <div className="space-y-4">
                <label className="block">
                  <span className="form-label text-xs">
                    Template de mensagem
                  </span>
                  <select
                    className="form-input"
                    value={templateId}
                    onChange={(e) => {
                      setTemplateId(e.target.value);
                      setMappings({});
                    }}
                  >
                    <option value="">Selecione um modelo aprovado</option>
                    {templates.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.element_name}
                      </option>
                    ))}
                  </select>
                </label>
                {selected?.parameter_positions.map((position) => (
                  <label className="block" key={position}>
                    <span className="form-label text-xs">
                      Parâmetro {`{{${position}}}`}
                    </span>
                    <select
                      className="form-input"
                      value={mappings[position] || ""}
                      onChange={(e) =>
                        setMappings((current) => ({
                          ...current,
                          [position]: e.target.value,
                        }))
                      }
                    >
                      <option value="">Selecione um campo do contato</option>
                      {fields.map((field) => (
                        <option key={field.key} value={field.key}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            ))}
          {step === 3 && (
            <fieldset className="space-y-3">
              <legend className="sr-only">Quando enviar?</legend>
              <label
                className={`flex gap-3 rounded-xl border p-4 ${scheduleMode === "NOW" ? "border-blue-500 bg-blue-50" : ""}`}
              >
                <input
                  type="radio"
                  className="accent-blue-600"
                  checked={scheduleMode === "NOW"}
                  onChange={() => setScheduleMode("NOW")}
                />
                <span>
                  <strong className="block text-sm">
                    Enviar após confirmação
                  </strong>
                  <small className="text-slate-500">
                    Inicia o processamento da campanha.
                  </small>
                </span>
              </label>
              <label
                className={`flex gap-3 rounded-xl border p-4 ${scheduleMode === "LATER" ? "border-blue-500 bg-blue-50" : ""}`}
              >
                <input
                  type="radio"
                  className="accent-blue-600"
                  checked={scheduleMode === "LATER"}
                  onChange={() => setScheduleMode("LATER")}
                />
                <span className="flex-1">
                  <strong className="block text-sm">Agendar envio</strong>
                  <small className="text-slate-500">
                    Defina a data e o horário
                  </small>
                  {scheduleMode === "LATER" && (
                    <input
                      aria-label="Data e hora do envio"
                      type="datetime-local"
                      className="form-input mt-3"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                    />
                  )}
                </span>
              </label>
              <p className="rounded-xl border bg-slate-50 p-4 text-[11px] leading-5 text-slate-500">
                O disparo respeita a capacidade de envio da conta e a cadência
                configurada.
              </p>
            </fieldset>
          )}
          {step === 4 && (
            <dl className="text-sm">
              {[
                ["Nome", name],
                ["Público", "Todos os contatos com aceite"],
                ["Contatos elegíveis", String(audience ?? 0)],
                ["Template", selected?.element_name || "—"],
                [
                  "Envio",
                  scheduleMode === "NOW"
                    ? "Após confirmação"
                    : new Date(scheduledAt).toLocaleString("pt-BR"),
                ],
              ].map(([term, value]) => (
                <div
                  className="flex justify-between gap-4 border-b py-3"
                  key={term}
                >
                  <dt className="text-slate-500">{term}</dt>
                  <dd className="text-right font-semibold">{value}</dd>
                </div>
              ))}
              <p className="mt-5 rounded-xl border bg-slate-50 p-4 text-[11px] leading-5 text-slate-500">
                A elegibilidade e os valores dos parâmetros serão revalidados
                durante o processamento.
              </p>
            </dl>
          )}
          {error && (
            <p
              role="alert"
              className="mt-4 rounded-lg bg-red-50 p-3 text-xs text-red-700"
            >
              {error}
            </p>
          )}
        </div>
        <WhatsAppPreview
          appName={user.appName}
          ctaLabels={preview.ctaLabels}
          message={preview.message}
          description="Prévia ilustrativa de um template de marketing. O envio real exige template aprovado pela Meta."
        />
      </div>
      <div className="mt-5 flex justify-between">
        <button
          className="btn btn-secondary"
          disabled={step === 0 || submitting}
          onClick={goBack}
        >
          <ArrowLeft size={15} /> Voltar
        </button>
        {step < 4 ? (
          <button
            className="btn btn-primary"
            disabled={!canAdvance || submitting}
            onClick={next}
          >
            Continuar <ArrowRight size={15} />
          </button>
        ) : (
          <button
            className="btn btn-primary"
            disabled={submitting}
            onClick={confirm}
          >
            {submitting ? "Confirmando..." : "Confirmar campanha ✓"}
          </button>
        )}
      </div>
    </section>
  );
}
